import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSubjects, getChapterTests, startChapterTest } from '../../api';

// Chapterwise Tests: every chapter is split into fixed papers of at most 90
// questions. A paper always holds the SAME questions, so retaking it shows real
// improvement rather than a different draw of luck.

function ScorePill({ pct }) {
  if (pct == null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const cls = pct >= 80 ? 'badge-green' : pct < 50 ? 'badge-red' : 'badge-yellow';
  return <span className={`badge ${cls}`}>{Math.round(pct)}%</span>;
}

export default function ChapterTests() {
  const navigate = useNavigate();
  const [subjectId, setSubjectId] = useState('');
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const { data: subjects } = useQuery({
    queryKey: ['subjects'],
    queryFn: () => getSubjects().then(r => r.data),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['chapterTests', subjectId],
    queryFn: () => getChapterTests(subjectId || undefined).then(r => r.data),
  });

  const begin = async (setId) => {
    setErr(''); setBusy(true);
    try {
      const r = await startChapterTest(setId);
      navigate(`/student/test/${r.data.test_id}`);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not start that test.');
      setBusy(false);
    }
  };

  const chapters = data?.chapters || [];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 28 }}>📚 Chapter Tests</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          Every chapter is split into papers of up to 90 questions. Each paper always has the
          same questions, so when you take it again you can see exactly how much you have improved.
        </p>
      </div>

      {err && <div className="card" style={{ borderColor: 'var(--danger)', color: 'var(--danger)', marginBottom: 16 }}>{err}</div>}

      <div className="card" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <label style={{ fontSize: 13, color: 'var(--text-muted)' }}>Subject</label>
        <select value={subjectId} onChange={e => { setSubjectId(e.target.value); setOpen(null); }}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)' }}>
          <option value="">All subjects</option>
          {(subjects || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {isLoading ? <div className="spinner"></div> : chapters.length === 0 ? (
        <div className="card"><div className="empty-state">No chapter tests yet.</div></div>
      ) : chapters.map(ch => {
        const isOpen = open === ch.chapter_id;
        const attempted = ch.tests.filter(t => t.attempts > 0).length;
        return (
          <div className="card" key={ch.chapter_id} style={{ marginBottom: 12 }}>
            <div onClick={() => setOpen(isOpen ? null : ch.chapter_id)}
                 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{ch.chapter_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {ch.subject_name} · {ch.question_count} questions · {ch.tests.length} test{ch.tests.length === 1 ? '' : 's'}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className={`badge ${attempted === ch.tests.length && ch.tests.length ? 'badge-green' : 'badge-yellow'}`}>
                  {attempted}/{ch.tests.length} attempted
                </span>
                <span style={{ color: 'var(--text-muted)' }}>{isOpen ? '▲' : '▼'}</span>
              </div>
            </div>

            {isOpen && (
              <div style={{ marginTop: 14, borderTop: '1px solid var(--border)' }}>
                {ch.tests.length === 0 ? (
                  <div className="empty-state" style={{ padding: '16px 0' }}>
                    No test has been built for this chapter yet.
                  </div>
                ) : ch.tests.map(t => (
                  <div key={t.set_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>Test {t.set_number}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {t.total_questions} questions · {t.total_questions} min
                        {t.attempts > 0 && ` · taken ${t.attempts} time${t.attempts === 1 ? '' : 's'}`}
                        {t.last_attempt_at && ` · last ${new Date(t.last_attempt_at).toLocaleDateString()}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {t.attempts > 0 && (
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>best</div>
                          <ScorePill pct={t.best_accuracy} />
                        </div>
                      )}
                      <button className={`btn btn-sm ${t.attempts > 0 ? 'btn-ghost' : 'btn-primary'}`}
                              disabled={busy} onClick={() => begin(t.set_id)}>
                        {t.attempts > 0 ? 'Take again' : 'Start'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
